import { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Loader2, Image as ImageIcon, Save, X, QrCode, Languages, Sparkles, ExternalLink, Download } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { api, getAdminToken } from "../../api";
import ImageUploadField from "../../components/ImageUploadField";

const EMPTY = {
  museum_id: "", title: "", artist: "", year: "", image_url: "",
  qr_code: "", preview_text_it: "", full_text_it: "",
  lat: null, lng: null, price_eur: null,
};

export default function Artworks() {
  const [items, setItems] = useState([]);
  const [museums, setMuseums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [filterMuseum, setFilterMuseum] = useState("");
  const [qrPreview, setQrPreview] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [generating, setGenerating] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [arts, mus] = await Promise.all([
        api.adminListArtworks(filterMuseum || undefined),
        api.adminListMuseums(),
      ]);
      setItems(arts.items || []);
      setMuseums(mus.items || []);
    } catch (e) {
      toast.error("Errore", { description: e.message });
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, [filterMuseum]); // eslint-disable-line

  function getMuseumName(mid) {
    return museums.find((m) => m.id === mid)?.name || mid;
  }

  async function save() {
    const body = {
      museum_id: editing.museum_id,
      title: editing.title,
      artist: editing.artist,
      year: editing.year,
      image_url: editing.image_url,
      qr_code: editing.qr_code || undefined,
      preview_text_it: editing.preview_text_it,
      full_text_it: editing.full_text_it,
      lat: editing.lat === "" ? null : editing.lat ? Number(editing.lat) : null,
      lng: editing.lng === "" ? null : editing.lng ? Number(editing.lng) : null,
      price_eur: editing.price_eur === "" || editing.price_eur === null ? null : Number(editing.price_eur),
    };
    try {
      if (editing.id) {
        await api.adminUpdateArtwork(editing.id, body);
        toast.success("Opera aggiornata");
      } else {
        await api.adminCreateArtwork(body);
        toast.success("Opera creata");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Salvataggio fallito", { description: e.message });
    }
  }

  async function remove(id) {
    if (!window.confirm("Eliminare opera?")) return;
    try {
      await api.adminDeleteArtwork(id);
      toast.success("Eliminata");
      load();
    } catch (e) { toast.error(e.message); }
  }

  async function translate() {
    if (!editing?.id) return toast.warning("Salva prima l'opera");
    setTranslating(true);
    try {
      await api.adminTranslate(editing.id);
      toast.success("Traduzioni completate");
      const fresh = await api.adminGetArtwork(editing.id);
      setEditing(fresh);
    } catch (e) { toast.error("Traduzione fallita", { description: e.message }); }
    finally { setTranslating(false); }
  }

  async function generate() {
    if (!editing?.title) return toast.warning("Inserisci almeno il titolo");
    setGenerating(true);
    try {
      const r = await api.adminGenerateText({
        title: editing.title, artist: editing.artist, year: editing.year, museum_id: editing.museum_id,
      });
      setEditing({ ...editing, preview_text_it: r.preview_text_it, full_text_it: r.full_text_it });
      toast.success("Testi generati con AI");
    } catch (e) { toast.error("Generazione fallita", { description: e.message }); }
    finally { setGenerating(false); }
  }

  async function showQr(id) {
    try {
      const r = await api.adminGetArtworkQr(id);
      setQrPreview(r);
    } catch (e) { toast.error(e.message); }
  }

  return (
    <div className="p-8 page-enter">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-4xl text-ink">Opere</h1>
          <p className="text-sm text-ink-soft mt-1">{items.length} opere totali</p>
        </div>
        <div className="flex gap-2 items-center">
          <select value={filterMuseum} onChange={(e) => setFilterMuseum(e.target.value)} className="px-3 py-2 bg-white border border-border rounded-xl text-sm">
            <option value="">Tutti i luoghi</option>
            {museums.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <button data-testid="add-artwork-btn" onClick={() => setEditing({ ...EMPTY, museum_id: filterMuseum || museums[0]?.id || "" })} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-5 py-2.5 rounded-full font-semibold transition">
            <Plus size={18} /> Nuova Opera
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="animate-spin text-brand" /></div>
      ) : (
        <div className="bg-white border border-border rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg/50">
              <tr className="text-left">
                <th className="px-4 py-3 text-[11px] tracking-widest font-semibold text-ink-soft">OPERA</th>
                <th className="px-4 py-3 text-[11px] tracking-widest font-semibold text-ink-soft">LUOGO</th>
                <th className="px-4 py-3 text-[11px] tracking-widest font-semibold text-ink-soft">QR</th>
                <th className="px-4 py-3 text-[11px] tracking-widest font-semibold text-ink-soft">PLAYS</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`artwork-row-${a.id}`} className="border-t border-border hover:bg-bg/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {a.image_url ? (
                        <img
                          src={a.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL || ""}${a.image_url}` : a.image_url}
                          alt=""
                          onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : null}
                      <div className={`w-12 h-12 rounded-lg bg-muted items-center justify-center ${a.image_url ? "hidden" : "flex"}`}>
                        <ImageIcon size={16} className="text-ink-soft" />
                      </div>
                      <div>
                        <div className="font-semibold text-ink">{a.title}</div>
                        <div className="text-xs text-ink-soft">{a.artist} · {a.year}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-ink-soft">{getMuseumName(a.museum_id)}</td>
                  <td className="px-4 py-3"><code className="text-xs bg-bg px-2 py-1 rounded">{a.qr_code}</code></td>
                  <td className="px-4 py-3 tabular-nums text-ink-soft">{a.plays || 0}</td>
                  <td className="px-4 py-3 flex gap-1.5 justify-end">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/artworks/${a.id}/qr.svg`}
                      download={`qrtourist-${a.qr_code}.svg`}
                      target="_blank"
                      rel="noreferrer"
                      title="Scarica QR vettoriale (laser/plotter)"
                      className="w-8 h-8 rounded-lg bg-bg hover:bg-gold/15 hover:text-gold flex items-center justify-center transition"
                      data-testid={`download-svg-${a.id}`}
                    ><Download size={14} /></a>
                    <Link to={`/print/${a.qr_code}`} target="_blank" className="w-8 h-8 rounded-lg bg-bg hover:bg-brand/10 hover:text-brand flex items-center justify-center transition" title="Stampa cartello A6"><ExternalLink size={14} /></Link>
                    <button onClick={() => showQr(a.id)} className="w-8 h-8 rounded-lg bg-bg hover:bg-brand/10 hover:text-brand flex items-center justify-center transition" title="Anteprima QR"><QrCode size={14} /></button>
                    <button onClick={() => setEditing({ ...a, preview_text_it: a.preview_texts?.it || "", full_text_it: a.full_texts?.it || "" })} className="w-8 h-8 rounded-lg bg-bg hover:bg-brand/10 hover:text-brand flex items-center justify-center transition"><Edit2 size={14} /></button>
                    <button onClick={() => remove(a.id)} className="w-8 h-8 rounded-lg bg-bg hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <div className="p-8 text-center text-sm text-ink-soft">Nessuna opera</div>}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[92vh] overflow-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-white z-10">
              <h2 className="font-heading font-bold text-2xl text-ink">{editing.id ? "Modifica" : "Nuova"} Opera</h2>
              <button onClick={() => setEditing(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-bg"><X /></button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4">
              <Field label="Luogo" full>
                <select value={editing.museum_id} onChange={(e) => setEditing({ ...editing, museum_id: e.target.value })} className="form-input">
                  <option value="">— seleziona —</option>
                  {museums.map((m) => <option key={m.id} value={m.id}>{m.name}{m.kind === "city" ? " (borgo)" : ""}</option>)}
                </select>
              </Field>
              <Field label="Titolo" full>
                <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="form-input" />
              </Field>
              <Field label="Artista">
                <input value={editing.artist} onChange={(e) => setEditing({ ...editing, artist: e.target.value })} className="form-input" />
              </Field>
              <Field label="Anno">
                <input value={editing.year} onChange={(e) => setEditing({ ...editing, year: e.target.value })} className="form-input" />
              </Field>
              <Field label="Foto opera" full>
                <ImageUploadField
                  value={editing.image_url}
                  onChange={(v) => setEditing({ ...editing, image_url: v })}
                  testId="artwork-image-upload"
                />
              </Field>
              <Field label="QR Code">
                <input value={editing.qr_code || ""} onChange={(e) => setEditing({ ...editing, qr_code: e.target.value })} placeholder="auto" className="form-input" />
              </Field>
              <Field label="Prezzo singolo (€)">
                <input type="number" step="0.01" value={editing.price_eur ?? ""} onChange={(e) => setEditing({ ...editing, price_eur: e.target.value })} placeholder="4.00" className="form-input" />
              </Field>
              <div className="col-span-2 flex items-center justify-between mt-2">
                <label className="text-[11px] tracking-widest font-semibold text-ink-soft">TESTI (IT)</label>
                <div className="flex gap-2">
                  <button onClick={generate} disabled={generating} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gold hover:bg-gold-hover text-white text-xs font-semibold rounded-lg disabled:opacity-60 transition">
                    {generating ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />} Genera con AI
                  </button>
                  <button onClick={translate} disabled={translating || !editing.id} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand hover:bg-brand-hover text-white text-xs font-semibold rounded-lg disabled:opacity-60 transition">
                    {translating ? <Loader2 className="animate-spin" size={14} /> : <Languages size={14} />} Traduci tutto
                  </button>
                </div>
              </div>
              <Field label="Anteprima (preview)" full>
                <textarea value={editing.preview_text_it} onChange={(e) => setEditing({ ...editing, preview_text_it: e.target.value })} rows={3} className="form-input resize-none" />
              </Field>
              <Field label="Testo completo (full)" full>
                <textarea value={editing.full_text_it} onChange={(e) => setEditing({ ...editing, full_text_it: e.target.value })} rows={6} className="form-input resize-none" />
              </Field>
              <Field label="Latitudine">
                <input type="number" step="any" value={editing.lat ?? ""} onChange={(e) => setEditing({ ...editing, lat: e.target.value })} className="form-input" />
              </Field>
              <Field label="Longitudine">
                <input type="number" step="any" value={editing.lng ?? ""} onChange={(e) => setEditing({ ...editing, lng: e.target.value })} className="form-input" />
              </Field>
            </div>
            <div className="flex gap-2 justify-end px-6 py-4 border-t border-border sticky bottom-0 bg-white">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-xl bg-bg text-ink-soft font-medium hover:bg-border transition">Annulla</button>
              <button data-testid="save-artwork-btn" onClick={save} className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white px-5 py-2 rounded-xl font-semibold transition"><Save size={16} /> Salva</button>
            </div>
          </div>
        </div>
      )}

      {qrPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setQrPreview(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
            <img src={`data:${qrPreview.mime};base64,${qrPreview.image_b64}`} alt="QR" className="w-64 h-64 mx-auto" />
            <div className="font-mono text-sm mt-3 text-ink">{qrPreview.qr_code}</div>
            <a href={qrPreview.url} target="_blank" rel="noreferrer" className="text-xs text-brand underline mt-1 block">{qrPreview.url}</a>
            <button onClick={() => setQrPreview(null)} className="mt-4 px-5 py-2 rounded-xl bg-brand text-white font-semibold w-full">Chiudi</button>
          </div>
        </div>
      )}

      <style>{`.form-input{width:100%;padding:.65rem .9rem;border:1px solid hsl(var(--border));border-radius:.7rem;background:#fff;font-size:.875rem;color:#1c1c1e;outline:none;transition:.15s}.form-input:focus{border-color:#0B4A99}`}</style>
    </div>
  );
}

function Field({ label, full, children }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="text-[11px] tracking-widest font-semibold text-ink-soft block mb-1.5">{label.toUpperCase()}</label>
      {children}
    </div>
  );
}
